import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SidebarPanelComponent } from '../../sidebar/sidebar-panel/sidebar-panel.component';
import { LandingPageTile } from '../../shared/tiles/home-tile.model';
import { SidebarService } from '../../shared/sidebar/sidebar.service';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { _ } from '../../shared/utils';
import { Subject, Observable, interval } from 'rxjs';
import { ReminderService } from '../reminder-service/reminder.service';
import { Reminder } from '../reminder.model';
import { takeUntil, map, distinctUntilChanged } from 'rxjs/operators';
import { BackendReminder } from '../reminder-fields.type';
import { AuthService } from '../../auth/auth.service';
import { NgbDate, NgbCalendar, NgbTimeStruct, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { DateParserFormatter } from '../../shared/date-parser-formatter.service';

@Component({
  selector: 'reminder-manager',
  templateUrl: './reminder-manager.component.html',
  styleUrls: ['./reminder-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: NgbDateParserFormatter, useClass: DateParserFormatter}
  ]
})
export class ReminderManagerComponent extends SidebarPanelComponent implements OnInit, OnDestroy {
  /** Values to show as quick increment buttons below the time field */
  public readonly timeAddIntervals: number[] = [5, 10, 15, 20, 30];

  /** Emits the set of stored Reminders every time it changes */
  public reminders$: Observable<Reminder[]>;
  /** The reminder currently being edited, if any */
  public editedReminder: Reminder | null = null;
  /** Indicates that the editor content is currently being saved */
  public saving: boolean = false;

  /** Time specified in the editor form */
  public newTime: NgbTimeStruct;
  /** Date specified in the editor form */
  public newDate: NgbDate;
  /** Description specified in the editor form */
  public newDescription: string;
  /** Error message currently applying to the editor form, if any */
  public newError: string | null;
  /** Minimum date allowed by the editor form */
  public newMinDate: NgbDate;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();

  constructor(private changeDetector: ChangeDetectorRef,
              private reminderService: ReminderService,
              private authService: AuthService,
              private calendarService: NgbCalendar,
              protected sidebarService: SidebarService,
              protected appMessageService: AppMessageService) {
    super(appMessageService, sidebarService);
    this.componentName = ReminderManagerComponent.getComponentName();
    this.onClearClick();
  }

  /**
   * Configure the `LandingPageTile` for this component
   * @override
   */
  public static initTile(tile: LandingPageTile) {
    tile.description = _('Manage reminders');
    tile.icon = 'far fa-alarm-clock';
    tile.title = _('Reminders');
    tile.panel = ReminderManagerComponent.getComponentName();
  }

  /**
   * @returns {string} Name of the component, used for panel and authorization APIs
   */
  private static getComponentName(): string {
    return 'reminders';
  }

  /**
   * @implements {OnInit}
   */
  ngOnInit() {
    this.reminders$ = this.reminderService.activeReminders$.pipe(
      takeUntil(this.ngUnsubscribe),
      map(reminders => reminders.sort((a, b) => a.backendReminder.alarmingTime.getTime() - b.backendReminder.alarmingTime.getTime()))
    );

    // make sure time inputs don't end up in the past as time passes
    interval(1000).pipe(
      takeUntil(this.ngUnsubscribe),
      map(_ => new Date()),
      distinctUntilChanged((a, b) => a && b && a.getHours() === b.getHours() && a.getMinutes() === b.getMinutes())
    ).subscribe(now => {
      if (this.getNewDateTimeAsDate().getTime() < now.getTime()) {
        this.newTime = this.getNgbTime(now);
        this.newDate = this.getNgbDate(now);
        this.newMinDate = this.getNgbDate(now);
        this.changeDetector.markForCheck();
      }
    });
  }

  /**
   * @implements {OnDestroy}
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Increment the time in the edit form by the given number of minutes.
   * Handles overflowing into the hour and date fields when needed.
   * @param {number} minutes
   */
  onIncrementTimeClick(minutes: number) {
    const time: NgbTimeStruct = {
      hour: this.newTime.hour + Math.floor((this.newTime.minute + minutes) / 60),
      minute: (this.newTime.minute + minutes) % 60,
      second: 0
    };

    // overflow hours into the date field
    if (time.hour >= 24) {
      this.newDate = this.calendarService.getNext(this.newDate, 'd', Math.floor(time.hour / 24));
      time.hour %= 24;
    }

    this.newTime = time;
    this.changeDetector.markForCheck();
  }

  /**
   * Load the given reminder into the edit form.
   * If called on currently loaded item, clears the form.
   * @param {Reminder} reminder
   */
  onEditClick(reminder: Reminder) {
    // if edit is clicked on currently edited item, deselect it instead
    if (this.editedReminder && this.editedReminder.backendReminder.id === reminder.backendReminder.id) {
      this.onClearClick();
      return;
    }

    this.editedReminder = reminder;
    this.newDate = this.getNgbDate(reminder.backendReminder.alarmingTime);
    this.newTime = this.getNgbTime(reminder.backendReminder.alarmingTime);
    this.newDescription = reminder.backendReminder.description;
    this.newError = null;
    this.changeDetector.markForCheck();
  }

  /**
   * Clear the edit form and any errors messages it has.
   */
  onClearClick() {
    const now = new Date();
    this.editedReminder = null;
    this.newDate = this.getNgbDate(now);
    this.newTime = this.getNgbTime(now);
    this.newDescription = '';
    this.newError = null;
    this.newMinDate = this.getNgbDate(now);
    this.changeDetector.markForCheck();
  }

  /**
   * Store the reminder specified by the form in the backend and clears the form.
   * If the form was filled using an edit button, edits the existing reminder,
   * otherwise creates a new one. Performs rudimentary validation on the inputs.
   */
  async onSaveClick() {
    const data = {
      userId: this.authService.getSubject(),
      alarmingTime: this.getNewDateTimeAsDate(),
      description: this.newDescription,
      isActive: true
    } as BackendReminder;

    // Mark early in case an error causes an early return,
    // and to render while awaiting the backend operation
    this.changeDetector.markForCheck();

    if (data.alarmingTime.getTime() <= new Date().getTime()) {
      this.newError = _('Time must be in the future!');
      return;
    }

    if (!data.description) {
      this.newError = _('Description is required!');
      return;
    }

    this.saving = true;
    try {
      if (this.editedReminder) {
        await this.reminderService.editReminder(Object.assign({}, this.editedReminder.backendReminder, data));
      } else {
        await this.reminderService.createReminder(data);
      }
    } finally {
      this.saving = false;
    }

    this.onClearClick();
  }

  /**
   * Cancel the given reminder.
   * @param {Reminder} reminder
   */
  onDeleteClick(reminder: Reminder) {
    // if currently edited item is deleted, deselect it first
    if (this.editedReminder && this.editedReminder.backendReminder.id === reminder.backendReminder.id) {
      this.onClearClick();
    }

    reminder.cancelAlarm();
    this.changeDetector.markForCheck();
  }

  /**
   * @returns {Date} The time specified by the edit form's time and date fields
   */
  getNewDateTimeAsDate(): Date {
    return new Date(this.newDate.year, this.newDate.month - 1, this.newDate.day, this.newTime.hour, this.newTime.minute);
  }

  /**
   * Convert a `Date` into an `NgbDate`.
   * @param {Date} date The date to convert
   * @returns {NgbDate}
   */
  getNgbDate(date: Date): NgbDate {
    return new NgbDate(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  }

  /**
   * Convert a `Date` into an `NgbTimeStruct`, discarding seconds.
   * @param {Date} date The time to convert
   * @returns {NgbTimeStruct} The given time, except `second` is always 0
   */
  getNgbTime(time: Date): NgbTimeStruct {
    return {
      hour: time.getHours(),
      minute: time.getMinutes(),
      second: 0
    };
  }
}
